import React, { useEffect, useRef, useState } from "react";
import { RCCButton, RCCGrid, RCCHookToast, RCCIcon, RCCPopover, RCCRefSpreadsheetProps } from "react-common-components";
import { useFieldArray } from "react-hook-form";
import { useTranslation } from "src/context/Translations/context";
import Spreadsheet from "src/components/Spreadsheet";
import stringToNumber from "src/utils/stringToNumber";
import InputNumber from "src/components/InputNumber";
import { EvaluationForm } from "src/components/ModalIndicator/interface";
import Title from "src/components/Title";
import { completePermissionType } from "src/components/Permissions/interface";
import formatNumber from "src/utils/formatNumber";
import { IGoalManagementForm, IGoalValuestab } from "./interface";

const GoalValuestab: React.FC<IGoalValuestab> = ({ 
	goal,
	objectName,
	register,
	control,
	setValue,
	permission,
	errors,
	getValue
}) => {
	const { translate,currentLanguage} = useTranslation();
	const { addToast} = RCCHookToast(); 
	const spreadsheetRef = useRef<RCCRefSpreadsheetProps>(null);
	const [spreadsheetData, setSpreadsheetData] = useState<string[][]>([[]]);
	const [editGoal,setEditGoal] = useState<boolean>(true);
	const [editValues,setEditValues] = useState<boolean>(true);
			
	const {fields,append} = useFieldArray<IGoalManagementForm>({
		control,
		name: `${objectName}.values`,
		shouldUnregister:true
	});
	
	const calculateValue = () => {
		const defaultGoal = stringToNumber(getValue(`${objectName}.iqc002Metadefault`),currentLanguage);
		let growRate:number = stringToNumber(getValue(`${objectName}.growRate`),currentLanguage);
		if((!defaultGoal && defaultGoal !== 0) || (!growRate && growRate !== 0 )){
			addToast({
				color: "yellow",
				description: translate("O campo meta padrão e % de crescimento devem estar preenchidos"),
				title: translate("Atenção")
			});
			return;
		}
		growRate /=100;
		let lastValue = String(defaultGoal);
		const actualValues = spreadsheetRef.current?.getData() as string[][];

		const newValues = actualValues?.map((val,index) => {
			if(index !== 0){
				if(goal?.iqc001Formaaval === EvaluationForm.TO_UP || (defaultGoal < 0 && goal?.iqc001Formaaval === EvaluationForm.TO_DOWN)){
					lastValue = (Number(lastValue) * (1 + growRate)).toFixed(goal.iqb001Casasdecimais);
				}
				else{
					lastValue = (Number(lastValue) / (1 + growRate)).toFixed(goal?.iqb001Casasdecimais);
				}
			}
			setValue(`${objectName}.values.${index}.iqd001Valor`,lastValue);
			return [val[0],lastValue.toString(),val[2]];
		});
		setSpreadsheetData(newValues);
	};

	const updateValues = () => {
		if(!spreadsheetRef.current)
			return;	
		
		const actualValues:string[][] = spreadsheetRef.current?.getData() as string[][];
		actualValues?.forEach((val,index) => {
			const valueGoal = Number.isNaN(stringToNumber(val[1],currentLanguage)) ? "0" : stringToNumber(val[1],currentLanguage);
			setValue(`${objectName}.values.${index}.iqd001Valor`, valueGoal);
			setValue(`${objectName}.values.${index}.iqd001Comentario`,val[2]);
		});
	};

	const hasPermission = (requiredPermission:"goal"|"values") => (
		requiredPermission === "goal" ? 
			permission === completePermissionType.VIEW_AND_EDIT
			:
			permission === completePermissionType.VIEW_AND_CHANGE_VALUES_GOAL || 
			permission === completePermissionType.VIEW_AND_EDIT
	);

	useEffect(() => {
		if(goal && fields.length === 0)
			append(goal.values);
	}, []);

	useEffect(() => {
		setEditValues(!hasPermission("values"));
		setEditGoal(!hasPermission("goal"));
	}, [permission]);

	useEffect(() => {
		const newData:string[][] = [];
		goal?.values.forEach((val) => {
			newData.push([val.iqb004Descricao,val.iqd001Valor.toString(),val.iqd001Comentario]);
		});
		setSpreadsheetData(newData);
	}, [goal]);

	return(
		<RCCGrid container item justifyContent="center" spacing={2}>
			<RCCGrid container item>
				<RCCGrid container item sm={6}> 
					<input 
						{...register(`${objectName}.iqc002Id`)} 
						name={`${objectName}.iqc002Id`}
						value={goal?.iqc002Id||0}
						type="hidden"
					/>
					<input 
						{...register(`${objectName}.iqc001Id`)} 
						name={`${objectName}.iqc001Id`}
						value={goal?.iqc001Id||0}
						type="hidden"
					/>
					<input 
						{...register(`${objectName}.iqb001Id`)} 
						name={`${objectName}.iqb001Id`}
						value={goal?.iqb001Id||0}
						type="hidden"
					/>
					<InputNumber
						name={`${objectName}.iqc002Metadefault`}
						label={translate("Meta padrão")}
						defaultValue={goal?.iqc002Metadefault}
						disabled={editGoal}
						error={errors[objectName]?.iqc002Metadefault}
						control={control}
						required
						decimalScale={goal?.iqb001Casasdecimais}
					/>
				</RCCGrid>
				<RCCGrid container item sm={6} 
					wrap="nowrap" 
					alignItems="baseline" 
					styleInline={`&.MuiGrid-root {
									@media(min-width: 600px){padding-left: 8px}
								}`
					}> 
					<InputNumber
						name={`${objectName}.growRate`}
						label={translate("% de crescimento")}
						control={control}
						defaultValue={0}
						disabled={editValues}
						required
						decimalScale={goal?.iqb001Casasdecimais}
					/>
					<RCCButton
						color="green"
						size="xs"
						onClick={() => calculateValue()}
						disabled={editValues}
					>
						{translate("Calcular")}
					</RCCButton>
					<RCCPopover
						header={{title:translate("Calcular")}}
						body={{text:`${translate("Coloca a meta padrão no primeiro período e calcula os seguintes conforme o % de crescimento informado")}.`}}
						position="left"
					>
						<RCCIcon
							name="infoCircle"
						/>
					</RCCPopover>
				</RCCGrid>
			</RCCGrid>
			<RCCGrid container item spacing={1}>
				<Title color="purple">
					{translate("Metas por período")} 
				</Title>
				<RCCGrid container item>
					<Spreadsheet 
						ref={spreadsheetRef}
						data={spreadsheetData}
						columns={[
							{ type: "text", title: " ", width: 80, readOnly: true, wordWrap: true },
							{ type: "numeric", title: translate("Meta"), readOnly: editValues, width: 130,  decimal: "," },
							{ type: "text", title: translate("Comentário"), readOnly: editValues, width: 350, wordWrap: true}
						]}
						format={{
							columns: [1],
							functionToFormat: goal?.iqb001Casasdecimais === 0 ? (number: string | number) => {
								const numberFormat = number.toString().replace(".", "");
								return formatNumber(numberFormat, currentLanguage, goal?.iqb001Casasdecimais);
							} : undefined
						}}
						onChange={updateValues}
						decimalPlaces={goal?.iqb001Casasdecimais}
					/>
				</RCCGrid>
			</RCCGrid>
		</RCCGrid>
	);
};
export default GoalValuestab;
